import React from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, X, ChevronDown } from "lucide-react";

const slugify = (str) =>
    str.toLowerCase()
       .normalize("NFD").replace(/[̀-ͯ]/g, "")
       .replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").trim();

export default function AdminCategories() {
    const [items, setItems] = React.useState([]);
    const [editing, setEditing] = React.useState(null);
    const [expanded, setExpanded] = React.useState({});

    const load = React.useCallback(() => api.get("/admin/categories").then(r => setItems(r.data || [])), []);
    React.useEffect(() => { load(); }, [load]);

    const save = async () => {
        try {
            if (editing.category_id) await api.put(`/admin/categories/${editing.category_id}`, editing);
            else await api.post("/admin/categories", editing);
            toast.success("Salvat"); setEditing(null); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
    };
    const del = async (id) => {
        if (!window.confirm("Sigur ștergi categoria? Produsele rămân neatinse.")) return;
        await api.delete(`/admin/categories/${id}`);
        toast.success("Șters"); load();
    };

    const openNew = (parentId = null) => {
        setEditing({ name: "", slug: "", description: "", image: "", order: 0, active: true, specs_schema: [], parent_id: parentId });
    };
    const openEdit = (c) => setEditing(JSON.parse(JSON.stringify(c)));

    const toggleExpand = (catId) => {
        setExpanded(prev => ({ ...prev, [catId]: !prev[catId] }));
    };

    const topCats = items.filter(c => !c.parent_id);
    const childrenOf = (id) => items.filter(c => c.parent_id === id);

    return (
        <div data-testid="admin-categories">
            <div className="flex items-center justify-between mb-6">
                <div><div className="overline mb-1">Administrare</div><h1 className="font-heading text-3xl">Categorii</h1></div>
                <button onClick={() => openNew()} className="btn-primary flex items-center gap-2"><Plus className="h-4 w-4" /> Adaugă categorie principală</button>
            </div>

            <div className="card-soft overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted">
                        <tr>{["", "Imagine", "Nume", "Slug", "Specificații", "Ordine", "Activ", ""].map(h =>
                            <th key={h} className="text-left px-3 py-3 text-xs uppercase text-muted-foreground">{h}</th>
                        )}</tr>
                    </thead>
                    <tbody>
                        {topCats.map(parent => {
                            const kids = childrenOf(parent.category_id);
                            const isExpanded = !!expanded[parent.category_id];
                            const parentProductCount = items.filter(p => p.category_id === parent.category_id).length;
                            return (
                                <React.Fragment key={parent.category_id}>
                                    <tr className="border-t border-border hover:bg-muted/50">
                                        <td className="px-3 py-2">
                                            {kids.length > 0 ? (
                                                <button onClick={() => toggleExpand(parent.category_id)} className="p-0.5 text-muted-foreground hover:text-foreground">
                                                    <ChevronDown className={`h-3 w-3 transition-transform ${isExpanded ? "rotate-0" : "-rotate-90"}`} />
                                                </button>
                                            ) : <span className="inline-block w-4"></span>}
                                        </td>
                                        <td className="px-3 py-2">{parent.image ? <img src={parent.image} alt="" className="w-10 h-10 object-cover rounded" /> : "—"}</td>
                                        <td className="px-3 py-2 font-medium">
                                            <button
                                                onClick={() => window.location.href = `/admin/products?category=${parent.category_id}`}
                                                className="text-left hover:text-primary transition-colors"
                                                title="Vezi produsele"
                                            >
                                                {parent.name}
                                            </button>
                                            {parentProductCount > 0 && <span className="ml-2 text-xs text-muted-foreground">({parentProductCount} produse)</span>}
                                        </td>
                                        <td className="px-3 py-2 text-xs font-mono">{parent.slug}</td>
                                        <td className="px-3 py-2 text-xs">{parent.specs_schema?.length || 0} spec.</td>
                                        <td className="px-3 py-2">{parent.order}</td>
                                        <td className="px-3 py-2">{parent.active ? "✓" : "—"}</td>
                                        <td className="px-3 py-2 text-right whitespace-nowrap">
                                            <button onClick={() => openNew(parent.category_id)} className="btn-ghost text-xs" title="Adaugă subcategorie">
                                                <Plus className="h-3.5 w-3.5" />
                                            </button>
                                            <button onClick={() => openEdit(parent)} className="btn-ghost"><Edit2 className="h-4 w-4" /></button>
                                            <button onClick={() => del(parent.category_id)} className="btn-ghost text-destructive"><Trash2 className="h-4 w-4" /></button>
                                        </td>
                                    </tr>
                                    {/* Subcategories */}
                                    {isExpanded && kids.map(child => (
                                        <tr key={child.category_id} className="border-t border-border hover:bg-muted/50 bg-muted/20">
                                            <td className="px-3 py-2"></td>
                                            <td className="px-3 py-2 pl-8">{child.image ? <img src={child.image} alt="" className="w-8 h-8 object-cover rounded" /> : "—"}</td>
                                            <td className="px-3 py-2 font-medium text-sm">
                                                <span className="text-muted-foreground mr-1">↳</span>
                                                <button
                                                    onClick={() => window.location.href = `/admin/products?category=${child.category_id}`}
                                                    className="text-left hover:text-primary transition-colors"
                                                    title="Vezi produsele"
                                                >
                                                    {child.name}
                                                </button>
                                            </td>
                                            <td className="px-3 py-2 text-xs font-mono">{child.slug}</td>
                                            <td className="px-3 py-2 text-xs">{child.specs_schema?.length || 0} spec.</td>
                                            <td className="px-3 py-2">{child.order}</td>
                                            <td className="px-3 py-2">{child.active ? "✓" : "—"}</td>
                                            <td className="px-3 py-2 text-right whitespace-nowrap">
                                                <button onClick={() => openEdit(child)} className="btn-ghost"><Edit2 className="h-4 w-4" /></button>
                                                <button onClick={() => del(child.category_id)} className="btn-ghost text-destructive"><Trash2 className="h-4 w-4" /></button>
                                            </td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                        {topCats.length === 0 && (
                            <tr><td colSpan={8} className="px-3 py-10 text-center text-muted-foreground">Nicio categorie adăugată.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-5 border-b border-border">
                            <h2 className="font-heading text-xl">{editing.category_id ? "Editează categorie" : editing.parent_id ? "Subcategorie nouă" : "Categorie nouă"}</h2>
                            <button onClick={() => setEditing(null)}><X className="h-5 w-5" /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <input placeholder="Nume *" value={editing.name || ""} onChange={(e) => {
                                    const name = e.target.value;
                                    const autoSlug = slugify(name);
                                    const curSlug = editing.slug || "";
                                    const wasAuto = !curSlug || curSlug === slugify(editing.name || "");
                                    setEditing({ ...editing, name, slug: wasAuto ? autoSlug : curSlug });
                                }} className="input-soft" />
                                <input placeholder="Slug *" value={editing.slug || ""} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="input-soft" />
                                <div className="md:col-span-2">
                                    <label className="text-xs text-muted-foreground block mb-1">Categorie părinte (pentru subcategorii)</label>
                                    <select value={editing.parent_id || ""} onChange={(e) => setEditing({ ...editing, parent_id: e.target.value || null })}
                                        className="input-soft"
                                    >
                                        <option value="">— Categorie principală —</option>
                                        {items.filter(c => c.category_id !== editing.category_id).map(c => (
                                            <option key={c.category_id} value={c.category_id}>{c.name} ({c.slug})</option>
                                        ))}
                                    </select>
                                </div>
                                <textarea placeholder="Descriere" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows="2" className="input-soft md:col-span-2" />
                                <div className="md:col-span-2">
                                    <label className="text-xs text-muted-foreground block mb-1">Imagine categorie</label>
                                    <div className="flex gap-2 items-center">
                                        {editing.image && <img src={editing.image?.startsWith("http") ? editing.image : editing.image} alt="" className="w-16 h-16 object-cover rounded-lg border border-border" />}
                                        <input value={editing.image || ""} onChange={(e) => setEditing({ ...editing, image: e.target.value })} placeholder="URL imagine" className="input-soft flex-1" />
                                        <label className="btn-outline cursor-pointer text-sm flex items-center gap-1">
                                            <Plus className="h-4 w-4" /> Încarcă
                                            <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                                                if (!e.target.files[0]) return;
                                                const fd = new FormData(); fd.append("file", e.target.files[0]);
                                                try {
                                                    const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                                                    setEditing(prev => prev ? { ...prev, image: res.data.url } : prev);
                                                    toast.success("Imagine încărcată");
                                                } catch { toast.error("Eroare upload"); }
                                            }} />
                                        </label>
                                    </div>
                                </div>
                                <input type="number" placeholder="Ordine" value={editing.order ?? 0} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} className="input-soft" />
                                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />Activ</label>
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-heading text-lg">Specificații</h3>
                                    <button type="button" onClick={() => setEditing({ ...editing, specs_schema: [...(editing.specs_schema || []), { key: "", label: "", type: "text", options: [], unit: "" }] })} className="btn-outline !py-2 text-sm flex items-center gap-1"><Plus className="h-3 w-3" /> Adaugă specificație</button>
                                </div>
                                <div className="space-y-3">
                                    {(editing.specs_schema || []).map((s, i) => (
                                        <div key={i} className="card-soft p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                                            <input placeholder="cheie (ex: resolution)" value={s.key} onChange={(e) => {
                                                const ns = [...editing.specs_schema]; ns[i] = { ...ns[i], key: e.target.value }; setEditing({ ...editing, specs_schema: ns });
                                            }} className="input-soft md:col-span-3 !py-2 text-sm" />
                                            <input placeholder="etichetă (ex: Rezoluție)" value={s.label} onChange={(e) => {
                                                const ns = [...editing.specs_schema]; ns[i] = { ...ns[i], label: e.target.value }; setEditing({ ...editing, specs_schema: ns });
                                            }} className="input-soft md:col-span-3 !py-2 text-sm" />
                                            <select value={s.type} onChange={(e) => {
                                                const ns = [...editing.specs_schema]; ns[i] = { ...ns[i], type: e.target.value }; setEditing({ ...editing, specs_schema: ns });
                                            }} className="input-soft md:col-span-2 !py-2 text-sm">
                                                <option value="text">Text</option>
                                                <option value="number">Număr</option>
                                                <option value="select">Select</option>
                                                <option value="boolean">Da/Nu</option>
                                            </select>
                                            <input placeholder="opțiuni (sep virgulă)" value={(s.options || []).join(", ")} onChange={(e) => {
                                                const ns = [...editing.specs_schema]; ns[i] = { ...ns[i], options: e.target.value.split(",").map(x => x.trim()).filter(Boolean) }; setEditing({ ...editing, specs_schema: ns });
                                            }} disabled={s.type !== "select"} className="input-soft md:col-span-2 !py-2 text-sm disabled:opacity-40" />
                                            <input placeholder="unitate" value={s.unit || ""} onChange={(e) => {
                                                const ns = [...editing.specs_schema]; ns[i] = { ...ns[i], unit: e.target.value }; setEditing({ ...editing, specs_schema: ns });
                                            }} className="input-soft md:col-span-1 !py-2 text-sm" />
                                            <button type="button" onClick={() => {
                                                const ns = editing.specs_schema.filter((_, j) => j !== i);
                                                setEditing({ ...editing, specs_schema: ns });
                                            }} className="btn-ghost text-destructive md:col-span-1"><Trash2 className="h-4 w-4" /></button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="p-5 border-t border-border flex justify-end gap-2">
                            <button onClick={() => setEditing(null)} className="btn-outline">Anulează</button>
                            <button onClick={save} className="btn-primary">Salvează</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
